/**
 * Part 9 — Governance Surfaces service.
 *
 * Wraps the /governance endpoints added by Part 9:
 *   - Strategic objectives / OKRs (A9.3)
 *   - Risk register rollup (A9.1)
 *   - Governance links (A9.1)
 *   - Unified decision log (A9.2)
 *   - Auto-narrative (A9.4)
 */
import { apiClient } from './api'

// ── OKRs ───────────────────────────────────────────────────────────────────
export interface KeyResult {
  id: number
  objective_id: number
  title: string
  unit: string | null
  start_value: number
  target_value: number
  current_value: number
  status: string
  position: number
  progress: number
  created_at: string | null
  updated_at: string | null
}

export interface Objective {
  id: number
  organization_id: number | null
  title: string
  description: string | null
  category: string | null
  status: string
  portfolio_id: number | null
  program_id: number | null
  owner_id: number | null
  period: string | null
  start_date: string | null
  target_date: string | null
  progress: number
  key_results: KeyResult[]
  created_at: string | null
  updated_at: string | null
}

export interface ObjectiveCreate {
  title: string
  description?: string | null
  category?: string | null
  status?: string
  portfolio_id?: number | null
  program_id?: number | null
  period?: string | null
  start_date?: string | null
  target_date?: string | null
  key_results?: Partial<KeyResult>[]
}

// ── Risk register ────────────────────────────────────────────────────────────
export interface RiskRegisterEntry {
  source: 'project' | 'program'
  source_id: number
  source_name: string | null
  raid_id: number
  title: string | null
  description: string | null
  status: string | null
  owner: string | null
  likelihood: number
  impact: number
  score: number
  severity: 'low' | 'medium' | 'high' | 'critical' | 'unscored'
  mitigation: string | null
  link_count: number
}

export interface RiskRegister {
  entries: RiskRegisterEntry[]
  total: number
  by_severity: Record<string, number>
  matrix: { likelihood: number; impact: number; count: number }[]
}

// ── Governance links ─────────────────────────────────────────────────────────
export interface GovernanceLink {
  id: number
  organization_id: number | null
  source_type: string
  source_id: number
  target_type: string
  target_id: number
  link_type: string
  note: string | null
  created_by_id: number | null
  created_at: string | null
}

// ── Decision log ──────────────────────────────────────────────────────────────
export interface DecisionLogEntry {
  source: 'project' | 'program'
  source_id: number
  source_name: string | null
  decision_id: number
  title: string
  status: string | null
  impact: string | null
  decided_at: string | null
  decision_maker: string | null
}

export interface DecisionLog {
  entries: DecisionLogEntry[]
  total: number
}

// ── Narrative ─────────────────────────────────────────────────────────────────
export interface NarrativePoint {
  metric: string
  current: number
  previous: number
  delta: number
  direction: 'up' | 'down' | 'flat'
  sentiment: 'positive' | 'negative' | 'neutral'
  text: string
}

export interface Narrative {
  scope: string
  scope_id: number | null
  period_days: number
  generated_at: string
  headline: string
  points: NarrativePoint[]
}

const base = '/api/v1/governance'

export const governanceService = {
  // OKRs
  listObjectives: (params: { portfolio_id?: number; program_id?: number; status?: string } = {}) =>
    apiClient.get<Objective[]>(`${base}/objectives`, { params }).then(r => r.data),
  createObjective: (body: ObjectiveCreate) =>
    apiClient.post<Objective>(`${base}/objectives`, body).then(r => r.data),
  getObjective: (id: number) =>
    apiClient.get<Objective>(`${base}/objectives/${id}`).then(r => r.data),
  updateObjective: (id: number, body: Partial<ObjectiveCreate>) =>
    apiClient.patch<Objective>(`${base}/objectives/${id}`, body).then(r => r.data),
  deleteObjective: (id: number) =>
    apiClient.delete(`${base}/objectives/${id}`).then(() => undefined),
  addKeyResult: (objId: number, body: Partial<KeyResult>) =>
    apiClient.post<KeyResult>(`${base}/objectives/${objId}/key-results`, body).then(r => r.data),
  updateKeyResult: (krId: number, body: Partial<KeyResult>) =>
    apiClient.patch<KeyResult>(`${base}/key-results/${krId}`, body).then(r => r.data),
  deleteKeyResult: (krId: number) =>
    apiClient.delete(`${base}/key-results/${krId}`).then(() => undefined),

  // Risk register
  getRiskRegister: (portfolio_id?: number) =>
    apiClient.get<RiskRegister>(`${base}/risk-register`, { params: { portfolio_id } }).then(r => r.data),

  // Links
  listLinks: (params: { source_type?: string; source_id?: number } = {}) =>
    apiClient.get<GovernanceLink[]>(`${base}/links`, { params }).then(r => r.data),
  createLink: (body: Omit<GovernanceLink, 'id' | 'organization_id' | 'created_by_id' | 'created_at'>) =>
    apiClient.post<GovernanceLink>(`${base}/links`, body).then(r => r.data),
  deleteLink: (id: number) =>
    apiClient.delete(`${base}/links/${id}`).then(() => undefined),

  // Decision log
  getDecisionLog: (portfolio_id?: number) =>
    apiClient.get<DecisionLog>(`${base}/decision-log`, { params: { portfolio_id } }).then(r => r.data),

  // Narrative
  getNarrative: (params: { portfolio_id?: number; period_days?: number } = {}) =>
    apiClient.get<Narrative>(`${base}/narrative`, { params }).then(r => r.data),
}
